import { useEffect, useState, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Badge,
  Button,
  Card,
  EmptyState,
  Spinner,
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
  Table, TableHeader, TableBody, TableHead, TableRow, TableCell,
} from '@agion/ui'
import { Database, Calendar, Clock, Download } from 'lucide-react'
import { PageHeader } from '@/layouts/InfraPageHeader'
import { listSchedules, listSnapshots } from './infra_backup.service'

interface BackupSchedule {
  id: string
  environment: string
  frequency: string
  lastRun: string | null
  lastRunStatus: 'success' | 'failed' | 'running' | null
  nextRun: string
}

interface BackupSnapshot {
  id: string
  name: string
  environment: string
  size: string
  createdAt: string
}

function statusBadgeVariant(status: BackupSchedule['lastRunStatus']) {
  switch (status) {
    case 'success':
      return 'success' as const
    case 'failed':
      return 'error' as const
    case 'running':
      return 'warning' as const
    default:
      return 'default' as const
  }
}

export function BackupsPage() {
  const { t } = useTranslation(['pages'])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [schedules, setSchedules] = useState<BackupSchedule[]>([])
  const [snapshots, setSnapshots] = useState<BackupSnapshot[]>([])

  const loadBackups = useCallback(async () => {
    try {
      setIsLoading(true)
      setError(null)
      const [schedulesRes, snapshotsRes] = await Promise.all([
        listSchedules(),
        listSnapshots(),
      ])
      setSchedules(schedulesRes.data.map(s => ({
        id: s.id,
        environment: s.environmentId,
        frequency: s.cronExpression,
        lastRun: s.lastRunAt ?? null,
        lastRunStatus: null,
        nextRun: s.nextRunAt ?? '',
      })))
      setSnapshots(snapshotsRes.data.map(s => ({
        id: s.id,
        name: s.id,
        environment: s.environmentId,
        size: s.sizeBytes ? `${Math.round(s.sizeBytes / 1024 / 1024)} MB` : '—',
        createdAt: s.startedAt,
      })))
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load backups')
    } finally {
      setIsLoading(false)
    }
  }, [])

  useEffect(() => {
    void loadBackups()
  }, [loadBackups])

  return (
    <div className="p-6 space-y-6">
      <PageHeader
        icon={Database}
        title={t('pages:backups.title', 'Backups')}
        description={t('pages:backups.description', 'Manage backup schedules and snapshots')}
      />

      {isLoading ? (
        <div className="flex items-center justify-center py-16">
          <Spinner />
        </div>
      ) : error ? (
        <Card>
          <div className="p-8 text-center text-red-600">{error}</div>
        </Card>
      ) : (
        <Tabs defaultValue="schedules">
          <TabsList>
            <TabsTrigger value="schedules">
              <Calendar className="h-4 w-4 mr-2" />
              {t('pages:backups.tabs.schedules', 'Schedules')}
            </TabsTrigger>
            <TabsTrigger value="snapshots">
              <Download className="h-4 w-4 mr-2" />
              {t('pages:backups.tabs.snapshots', 'Snapshots')}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="schedules">
            {schedules.length === 0 ? (
              <EmptyState
                icon={Calendar}
                title={t('pages:backups.schedules.empty', 'No backup schedules')}
                description={t('pages:backups.schedules.emptyDesc', 'Configure automatic backup schedules for your environments.')}
              />
            ) : (
              <Table variant="modern">
                <TableHeader variant="modern">
                  <TableRow variant="modern">
                    <TableHead variant="modern">{t('pages:backups.columns.environment', 'Environment')}</TableHead>
                    <TableHead variant="modern">{t('pages:backups.columns.frequency', 'Frequency')}</TableHead>
                    <TableHead variant="modern">{t('pages:backups.columns.lastRun', 'Last Run')}</TableHead>
                    <TableHead variant="modern">{t('pages:backups.columns.nextRun', 'Next Run')}</TableHead>
                    <TableHead variant="modern">{t('pages:backups.columns.status', 'Status')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody variant="modern">
                  {schedules.map((schedule) => (
                    <TableRow key={schedule.id} variant="modern">
                      <TableCell>{schedule.environment}</TableCell>
                      <TableCell className="text-slate-600 dark:text-slate-400">{schedule.frequency}</TableCell>
                      <TableCell className="text-slate-500 text-xs">
                        {schedule.lastRun ?? '-'}
                      </TableCell>
                      <TableCell className="text-slate-500 text-xs">
                        <Clock className="inline h-3 w-3 mr-1" />
                        {schedule.nextRun}
                      </TableCell>
                      <TableCell>
                        {schedule.lastRunStatus && (
                          <Badge variant={statusBadgeVariant(schedule.lastRunStatus)}>
                            {schedule.lastRunStatus}
                          </Badge>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </TabsContent>

          <TabsContent value="snapshots">
            {snapshots.length === 0 ? (
              <EmptyState
                icon={Download}
                title={t('pages:backups.snapshots.empty', 'No snapshots')}
                description={t('pages:backups.snapshots.emptyDesc', 'Snapshots will appear here after backups run.')}
              />
            ) : (
              <Table variant="modern">
                <TableHeader variant="modern">
                  <TableRow variant="modern">
                    <TableHead variant="modern">{t('pages:backups.columns.name', 'Name')}</TableHead>
                    <TableHead variant="modern">{t('pages:backups.columns.environment', 'Environment')}</TableHead>
                    <TableHead variant="modern">{t('pages:backups.columns.size', 'Size')}</TableHead>
                    <TableHead variant="modern">{t('pages:backups.columns.created', 'Created')}</TableHead>
                    <TableHead variant="modern">{t('pages:backups.columns.actions', 'Actions')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody variant="modern">
                  {snapshots.map((snapshot) => (
                    <TableRow key={snapshot.id} variant="modern">
                      <TableCell className="font-mono text-xs">{snapshot.name}</TableCell>
                      <TableCell className="text-slate-600 dark:text-slate-400">{snapshot.environment}</TableCell>
                      <TableCell className="text-slate-500">{snapshot.size}</TableCell>
                      <TableCell className="text-slate-500 text-xs">{snapshot.createdAt}</TableCell>
                      <TableCell>
                        <Button variant="outline" size="sm">
                          {t('pages:backups.actions.restore', 'Restore')}
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </TabsContent>
        </Tabs>
      )}
    </div>
  )
}
